import { provenanceDetailsForElement } from "./provenance-detail-bindings.js";
import { calculatorDataText } from "./calculator-data-i18n.js";
import {
    provenanceFactCardFactLabel,
    provenanceFactCardMarkup,
    provenanceFactCardPresentationStatus,
} from "./provenance-fact-card.js";
import {
    normalizeProvenanceClaims,
    PROVENANCE_CALLOUT_ID,
} from "./provenance-indicator.js";
import { attachAnchoredCalloutController } from "./anchored-callout-controller.js";

const tooltipRoots = new WeakSet();
let tooltipClaimSourceKey = null;
let tooltipElement = null;

function trimString(value) {
    return String(value ?? "").trim();
}

function escapeHtml(value) {
    return String(value ?? "").replace(
        /[&<>"']/g,
        (character) => ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            "\"": "&quot;",
            "'": "&#39;",
        })[character],
    );
}

function calloutLanes(claims) {
    const lanes = new Map();
    normalizeProvenanceClaims(claims).forEach((claim) => {
        const factLabel = provenanceFactCardFactLabel(claim.card);
        const lane = lanes.get(claim.laneId) ?? {
            factLabel,
            laneClaims: [],
            laneId: claim.laneId,
        };
        if (lane.factLabel !== factLabel) {
            throw new TypeError(
                `provenance lane ${claim.laneId} must use one consistent fact label`,
            );
        }
        lane.laneClaims.push(claim);
        lanes.set(claim.laneId, lane);
    });
    return [...lanes.values()].map(({ factLabel, laneClaims, laneId }) => {
        const effective = laneClaims.filter(({ status }) => status === "effective");
        if (effective.length !== 1) {
            throw new TypeError(
                `provenance lane ${laneId} must contain exactly one effective claim`,
            );
        }
        const nonEffectiveCandidates = laneClaims
            .filter(({ status }) => status !== "effective")
            .reverse();
        return Object.freeze({
            effectiveCard: effective[0].card,
            factLabel,
            laneId,
            visible: Object.freeze([effective[0], ...nonEffectiveCandidates]),
        });
    });
}

function calloutClaimNode({ card, status }, effectiveCard) {
    const presentedStatus = provenanceFactCardPresentationStatus({
        card,
        status,
        effectiveCard,
    });
    return `<li class="fishy-provenance-flow__node fishy-provenance-tooltip__claim" data-provenance-callout-claim-status="${escapeHtml(presentedStatus)}">`
        + provenanceFactCardMarkup({
            card,
            effectiveCard,
            mode: "callout",
            status,
        })
        + '</li>';
}

/**
 * Composes read-only provenance callouts from the canonical fact-card renderer.
 */
export function provenanceCalloutMarkup(claims) {
    return calloutLanes(claims)
        .map(({ effectiveCard, factLabel, laneId, visible }) => (
            `<section class="fishy-provenance-tooltip__lane" data-provenance-callout-lane="${escapeHtml(factLabel)}" data-provenance-callout-lane-id="${escapeHtml(laneId)}" aria-label="${escapeHtml(factLabel)}">`
            + '<ol class="fishy-provenance-flow fishy-provenance-tooltip__timeline" data-flow-direction="toward-start">'
            + visible.map((claim) => calloutClaimNode(claim, effectiveCard)).join("")
            + '</ol></section>'
        ))
        .join("");
}

function ensureTooltipElement() {
    if (tooltipElement?.isConnected) {
        return tooltipElement;
    }
    const documentRef = globalThis.document;
    if (!documentRef?.body || typeof documentRef.createElement !== "function") {
        return null;
    }
    tooltipElement = documentRef.createElement("div");
    tooltipElement.className = "fishy-provenance-tooltip";
    tooltipElement.hidden = true;
    tooltipElement.setAttribute("id", PROVENANCE_CALLOUT_ID);
    tooltipElement.setAttribute("role", "tooltip");
    const details = documentRef.createElement("div");
    details.className = "fishy-provenance-tooltip__details";
    details.setAttribute("tabindex", "-1");
    tooltipElement.appendChild(details);
    documentRef.body.appendChild(tooltipElement);
    return tooltipElement;
}

function provenanceTargetFromEvent(eventTarget) {
    if (!eventTarget || typeof eventTarget.closest !== "function") {
        return null;
    }
    return eventTarget.closest(
        "[data-fishy-provenance-claims], [data-fishy-provenance-binding]",
    );
}

function updateTooltipPosition(tooltip, anchor, event) {
    const windowRef = globalThis.window;
    if (!tooltip || !windowRef) {
        return;
    }
    let clientX = Number(event?.clientX);
    let clientY = Number(event?.clientY);
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
        const rect = typeof anchor?.getBoundingClientRect === "function"
            ? anchor.getBoundingClientRect()
            : null;
        clientX = Number(rect?.left ?? 0) + Number(rect?.width ?? 0) / 2;
        clientY = Number(rect?.top ?? 0) + Number(rect?.height ?? 0) / 2;
    }
    const offsetX = 14;
    const offsetY = 18;
    tooltip.style.left = "0";
    tooltip.style.top = "0";
    tooltip.style.transform = `translate3d(${clientX + offsetX}px, ${clientY + offsetY}px, 0)`;
    const tooltipRect = typeof tooltip.getBoundingClientRect === "function"
        ? tooltip.getBoundingClientRect()
        : null;
    if (!tooltipRect) {
        return;
    }
    const viewportWidth = Number(windowRef.innerWidth ?? 0);
    const viewportHeight = Number(windowRef.innerHeight ?? 0);
    let nextX = clientX + offsetX;
    let nextY = clientY + offsetY;
    if (viewportWidth > 0 && nextX + tooltipRect.width + 12 > viewportWidth) {
        nextX = Math.max(12, clientX - tooltipRect.width - 12);
    }
    if (viewportHeight > 0 && nextY + tooltipRect.height + 12 > viewportHeight) {
        nextY = Math.max(12, clientY - tooltipRect.height - 12);
    }
    tooltip.style.transform = `translate3d(${nextX}px, ${nextY}px, 0)`;
}

function claimsFromSerialized(value) {
    const serialized = trimString(value);
    if (!serialized) {
        return Object.freeze([]);
    }
    return normalizeProvenanceClaims(JSON.parse(serialized));
}

function presentProvenance(anchor) {
    const tooltip = ensureTooltipElement();
    if (!tooltip || !anchor?.dataset) {
        return null;
    }
    const boundDetails = provenanceDetailsForElement(anchor);
    const sourceKey = boundDetails
        ? boundDetails.claims
        : trimString(anchor.dataset.fishyProvenanceClaims);
    const claims = boundDetails
        ? normalizeProvenanceClaims(boundDetails.claims)
        : claimsFromSerialized(sourceKey);
    if (claims.length === 0) {
        return null;
    }
    const container = tooltip.querySelector(".fishy-provenance-tooltip__details");
    if (!container) {
        return null;
    }
    if (tooltipClaimSourceKey !== sourceKey || !container.innerHTML) {
        container.innerHTML = provenanceCalloutMarkup(claims);
    }
    tooltipClaimSourceKey = sourceKey;
    return {
        dialogLabel: calculatorDataText("common.provenance.label.default", "Provenance"),
        focusTarget: container,
        panel: tooltip,
    };
}

/**
 * Owns delegated provenance hover/focus previews and click-pinned callouts for
 * one event root.
 */
export function attachProvenanceTooltip(root) {
    if (!root || tooltipRoots.has(root) || typeof root.addEventListener !== "function") {
        return;
    }
    tooltipRoots.add(root);
    const controller = attachAnchoredCalloutController(root, {
        id: PROVENANCE_CALLOUT_ID,
        fallbackDialogLabel: calculatorDataText(
            "common.provenance.label.default",
            "Provenance",
        ),
        panel: () => tooltipElement,
        pinnedAttribute: "data-fishy-provenance-callout-pinned",
        present: presentProvenance,
        resolveAnchor: provenanceTargetFromEvent,
        onDismiss() {
            tooltipClaimSourceKey = null;
        },
        position({ panel }, anchor, event) {
            updateTooltipPosition(panel, anchor, event);
        },
    });
    if (!controller) {
        return null;
    }
    return Object.freeze({
        ...controller,
        dispose() {
            tooltipRoots.delete(root);
            controller.dispose();
        },
    });
}
